import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Avatar,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Menu,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  VisibilityOff as HiddenIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import visualAssetService, { SavedAsset, AssetType, DesignStyle } from '../../../../services/visualAssetService';
import trackingService, { SocialPlatform, AnalyticsSummary } from '../../../../services/trackingService';
import { format } from 'date-fns';
import useResponsiveDesign from '../../../../hooks/useResponsiveDesign';
import ResponsiveContainer from '../../../../components/responsive/ResponsiveContainer';

interface EventAssetsTableProps {
  organizationId: string;
  eventId: string;
  eventTitle?: string;
}

const EventAssetsTable: React.FC<EventAssetsTableProps> = ({
  organizationId,
  eventId,
  eventTitle
}) => {
  // Get responsive breakpoints and utilities
  const theme = useTheme();
  const { isMobile, isTablet, gridLayouts } = useResponsiveDesign();
  const [assets, setAssets] = useState<SavedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<SavedAsset | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedType, setEditedType] = useState<AssetType>('banner');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [trackableUrls, setTrackableUrls] = useState<Record<SocialPlatform, string>>({
    facebook: '',
    twitter: '',
    linkedin: '',
    instagram: '',
    email: '',
    whatsapp: '',
    direct: ''
  });
  const [sharingPlatform, setSharingPlatform] = useState<SocialPlatform | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsSummary | null>(null);

  // Fetch assets on mount
  useEffect(() => {
    fetchAssets();
  }, [organizationId, eventId]);

  // Fetch assets from the service
  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchedAssets = await visualAssetService.fetchEventAssets(organizationId, eventId);
      
      // Sort assets by creation date, newest first
      const sortedAssets = fetchedAssets.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      setAssets(sortedAssets);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to load visual assets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the edit dialog
  const handleEditClick = (asset: SavedAsset) => {
    setSelectedAsset(asset);
    setEditedTitle(asset.title);
    setEditedDescription(asset.description || '');
    setEditedType(asset.type);
    setEditDialogOpen(true);
  };

  // Handle saving asset edits
  const handleSaveAsset = async () => {
    if (!selectedAsset) return;
    
    try {
      setIsSaving(true);
      
      const updatedAsset: SavedAsset = {
        ...selectedAsset,
        title: editedTitle,
        description: editedDescription,
        type: editedType,
        updatedAt: new Date().toISOString()
      };
      
      const success = await visualAssetService.updateAsset(eventId, updatedAsset);
      
      if (success) {
        // Update local state to reflect changes
        setAssets(assets.map(asset => 
          asset.id === updatedAsset.id ? updatedAsset : asset
        ));
        
        setEditDialogOpen(false);
      } else {
        setError('Failed to update asset. Please try again.');
      }
    } catch (err) {
      console.error('Error updating asset:', err);
      setError('Failed to update asset. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting an asset
  const handleDeleteAsset = async () => {
    if (!selectedAsset) return;
    
    try {
      setIsDeleting(true);
      
      const success = await visualAssetService.deleteAsset(
        organizationId, 
        eventId, 
        selectedAsset.id
      );
      
      if (success) {
        // Update local state to remove the deleted asset
        setAssets(assets.filter(asset => asset.id !== selectedAsset.id));
        setDeleteDialogOpen(false);
      } else {
        setError('Failed to delete asset. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      setError('Failed to delete asset. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle copying an image URL
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopySuccess('URL copied to clipboard!');
        setTimeout(() => setCopySuccess(null), 2000);
      })
      .catch(err => {
        console.error('Error copying URL:', err);
        setError('Failed to copy URL');
      });
  };

  // Handle downloading an image
  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'event-asset.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Get type label for display
  const getTypeLabel = (type: AssetType) => {
    switch (type) {
      case 'banner': return 'Banner';
      case 'social': return 'Social Media';
      case 'flyer': return 'Promotional';
      default: return type;
    }
  };

  // Render type label with appropriate color
  const renderTypeChip = (type: AssetType) => {
    const chipColor = type === 'banner' ? 'primary' : 
                      type === 'social' ? 'info' : 
                      'secondary';
    
    return (
      <Chip 
        label={getTypeLabel(type)} 
        size="small" 
        color={chipColor}
        variant="outlined"
      />
    );
  };

  const handleShareMenuOpen = (event: React.MouseEvent<HTMLElement>, asset: SavedAsset) => {
    setSelectedAsset(asset);
    setShareMenuAnchorEl(event.currentTarget);
    generateTrackableUrls(asset);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchorEl(null);
  };

  const generateTrackableUrls = (asset: SavedAsset) => {
    // Base URL for the event
    // In a production app, this should be your actual domain
    const baseEventUrl = `${window.location.origin}/events/${eventId}`;
    
    // Generate trackable URLs for each platform
    const urls: Record<SocialPlatform, string> = {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      email: '',
      whatsapp: '',
      direct: ''
    };
    
    // Generate a trackable URL for each platform
    Object.keys(urls).forEach(platform => {
      const { shareUrl } = trackingService.generateTrackableUrl(
        baseEventUrl,
        asset.id,
        'asset',
        platform as SocialPlatform
      );
      urls[platform as SocialPlatform] = shareUrl;
    });
    
    setTrackableUrls(urls);
  };

  const handleSocialShare = async (platform: SocialPlatform) => {
    if (!selectedAsset) return;
    
    setIsSharing(true);
    setSharingPlatform(platform);
    
    try {
      // Get the trackable URL for this platform
      const trackableUrl = trackableUrls[platform];
      
      // Extract the tracking ID from the URL
      const url = new URL(trackableUrl);
      const trackingId = url.searchParams.get('share_id') || '';
      
      // Record the share in our database
      await trackingService.recordShare(
        selectedAsset.id,
        'asset',
        platform,
        trackableUrl,
        trackingId,
        organizationId,
        eventId,
        'current-user-id', // In a real app, get this from auth
        'Current User', // In a real app, get this from auth
        {
          title: selectedAsset.title,
          assetType: selectedAsset.type,
          imageUrl: selectedAsset.imageUrl
        }
      );
      
      // Open the appropriate sharing URL based on the platform
      let shareUrl = '';
      const encodedUrl = encodeURIComponent(trackableUrl);
      const encodedTitle = encodeURIComponent(selectedAsset.title);
      const encodedText = encodeURIComponent(`Check out this ${selectedAsset.type} for ${eventTitle || 'this event'}!`);
      
      switch (platform) {
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        case 'whatsapp':
          shareUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
          break;
        case 'email':
          shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`;
          break;
        case 'direct':
          // Just copy to clipboard
          await navigator.clipboard.writeText(trackableUrl);
          setShareSuccess('Trackable link copied to clipboard!');
          break;
      }
      
      // Open the share URL in a new window (except for direct links)
      if (platform !== 'direct' && shareUrl) {
        window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
        setShareSuccess(`Shared on ${platform}!`);
      }
      
      handleShareMenuClose();
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
      setError(`Failed to share to ${platform}. Please try again.`);
    } finally {
      setIsSharing(false);
      setSharingPlatform(null);
      
      // Clear success message after a delay
      if (shareSuccess) {
        setTimeout(() => {
          setShareSuccess(null);
        }, 3000);
      }
    }
  };

  const handleViewAnalytics = async (asset: SavedAsset) => {
    setSelectedAsset(asset);
    setAnalyticsDialogOpen(true);
    setAnalyticsLoading(true);
    
    try {
      // Fetch analytics data for this asset
      const analytics = await trackingService.getAssetAnalytics(eventId, asset.id);
      setAnalyticsData(analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </ResponsiveContainer>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography color="error">{error}</Typography>
        <Button 
          variant="outlined" 
          sx={{ mt: 2 }} 
          onClick={fetchAssets}
        >
          Try Again
        </Button>
      </Paper>
    );
  }

  if (assets.length === 0) {
    return (
      <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
        <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">No Assets Created Yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generate visual assets using the tools above to see them listed here.
        </Typography>
      </Paper>
    );
  }

  return (
    <ResponsiveContainer>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography color="error">{error}</Typography>
          <Button 
            variant="outlined" 
            sx={{ mt: 2 }} 
            onClick={fetchAssets}
          >
            Try Again
          </Button>
        </Paper>
      ) : assets.length === 0 ? (
        <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
          <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No Assets Created Yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate visual assets using the tools above to see them listed here.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Preview</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assets.map((asset) => (
                <TableRow key={asset.id}>
                  <TableCell sx={{ width: 100 }}>
                    <Box 
                      component="img" 
                      src={asset.imageUrl} 
                      alt={asset.title}
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        objectFit: 'cover',
                        cursor: 'pointer',
                        borderRadius: 1
                      }}
                      onClick={() => {
                        setSelectedAsset(asset);
                        setPreviewDialogOpen(true);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {asset.title}
                    </Typography>
                    {asset.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {asset.description.length > 100 
                          ? `${asset.description.substring(0, 100)}...` 
                          : asset.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {renderTypeChip(asset.type)}
                  </TableCell>
                  <TableCell>
                    {formatDate(asset.createdAt)}
                    {asset.updatedAt && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Updated: {formatDate(asset.updatedAt)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditClick(asset)}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Share">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleShareMenuOpen(e, asset)}
                          sx={{ mr: 0.5 }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy URL">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyUrl(asset.imageUrl)}
                          sx={{ mr: 0.5 }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownload(asset.imageUrl, `${asset.title}.png`)}
                          sx={{ mr: 0.5 }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Analytics">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewAnalytics(asset)}
                          sx={{ mr: 0.5 }}
                        >
                          <AnalyticsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => {
                            setSelectedAsset(asset);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {/* Edit Asset Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => !isSaving && setEditDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Edit Asset</DialogTitle>
          <DialogContent dividers>
            {selectedAsset && (
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={6}>
                  <Box 
                    component="img" 
                    src={selectedAsset.imageUrl} 
                    alt={selectedAsset.title}
                    sx={{ 
                      width: '100%', 
                      borderRadius: 1,
                      mb: 2,
                      maxHeight: 250,
                      objectFit: 'contain' 
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Title"
                    fullWidth
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    margin="normal"
                  />
                  
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="asset-type-label">Asset Type</InputLabel>
                    <Select
                      labelId="asset-type-label"
                      id="asset-type"
                      value={editedType}
                      label="Asset Type"
                      onChange={(e) => setEditedType(e.target.value as AssetType)}
                    >
                      <MenuItem value="banner">Event Banner</MenuItem>
                      <MenuItem value="social">Social Media Graphic</MenuItem>
                      <MenuItem value="flyer">Promotional Material</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={4}
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    margin="normal"
                  />
                </Grid>
                
                {/* Additional metadata */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(selectedAsset.createdAt)}
                  </Typography>
                  {selectedAsset.prompt && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Generation Prompt:
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {selectedAsset.prompt}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setEditDialogOpen(false)} 
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveAsset} 
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Asset Dialog */}
        <Dialog 
          open={previewDialogOpen} 
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>Asset Preview</DialogTitle>
          <DialogContent dividers>
            {selectedAsset && (
              <Box sx={{ textAlign: 'center' }}>
                <Box 
                  component="img" 
                  src={selectedAsset.imageUrl} 
                  alt={selectedAsset.title}
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh',
                    objectFit: 'contain' 
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="h6">{selectedAsset.title}</Typography>
                  {renderTypeChip(selectedAsset.type)}
                </Box>
                
                {selectedAsset.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'left' }}>
                    {selectedAsset.description}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              startIcon={<CopyIcon />} 
              onClick={() => selectedAsset && handleCopyUrl(selectedAsset.imageUrl)}
            >
              Copy URL
            </Button>
            <Button 
              startIcon={<DownloadIcon />} 
              onClick={() => selectedAsset && handleDownload(selectedAsset.imageUrl, `${selectedAsset.title}.png`)}
            >
              Download
            </Button>
            <Button 
              onClick={() => setPreviewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Asset</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this asset? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={handleDeleteAsset}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Menu */}
        <Menu
          anchorEl={shareMenuAnchorEl}
          open={Boolean(shareMenuAnchorEl)}
          onClose={handleShareMenuClose}
        >
          <List dense>
            <ListItem 
              button 
              onClick={() => handleSocialShare('facebook')}
              disabled={isSharing && sharingPlatform === 'facebook'}
            >
              <ListItemIcon>
                <FacebookIcon style={{ color: '#1877F2' }} />
              </ListItemIcon>
              <ListItemText primary="Facebook" />
              {isSharing && sharingPlatform === 'facebook' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
            <ListItem 
              button 
              onClick={() => handleSocialShare('twitter')}
              disabled={isSharing && sharingPlatform === 'twitter'}
            >
              <ListItemIcon>
                <TwitterIcon style={{ color: '#1DA1F2' }} />
              </ListItemIcon>
              <ListItemText primary="Twitter" />
              {isSharing && sharingPlatform === 'twitter' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
            <ListItem 
              button 
              onClick={() => handleSocialShare('linkedin')}
              disabled={isSharing && sharingPlatform === 'linkedin'}
            >
              <ListItemIcon>
                <LinkedInIcon style={{ color: '#0A66C2' }} />
              </ListItemIcon>
              <ListItemText primary="LinkedIn" />
              {isSharing && sharingPlatform === 'linkedin' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
            <ListItem 
              button 
              onClick={() => handleSocialShare('whatsapp')}
              disabled={isSharing && sharingPlatform === 'whatsapp'}
            >
              <ListItemIcon>
                <WhatsAppIcon style={{ color: '#25D366' }} />
              </ListItemIcon>
              <ListItemText primary="WhatsApp" />
              {isSharing && sharingPlatform === 'whatsapp' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
            <ListItem 
              button 
              onClick={() => handleSocialShare('email')}
              disabled={isSharing && sharingPlatform === 'email'}
            >
              <ListItemIcon>
                <EmailIcon style={{ color: '#EA4335' }} />
              </ListItemIcon>
              <ListItemText primary="Email" />
              {isSharing && sharingPlatform === 'email' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
            <Divider />
            <ListItem 
              button 
              onClick={() => handleSocialShare('direct')}
              disabled={isSharing && sharingPlatform === 'direct'}
            >
              <ListItemIcon>
                <LinkIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Copy Trackable Link" />
              {isSharing && sharingPlatform === 'direct' && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </ListItem>
          </List>
        </Menu>

        {/* Analytics Dialog */}
        <Dialog
          open={analyticsDialogOpen}
          onClose={() => setAnalyticsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            Asset Performance Analytics
            {selectedAsset && (
              <Typography variant="subtitle1" color="text.secondary">
                {selectedAsset.title}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {analyticsLoading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 1,
                justifyContent: 'center', 
                marginTop: 3 
              }}>
                <CircularProgress />
              </Box>
            ) : analyticsData ? (
              <Box sx={{ mt: 2 }}>
                {/* Summary Metrics */}
                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" align="center">
                        Total Shares
                      </Typography>
                      <Typography variant="h3" align="center" color="primary">
                        {analyticsData.totalShares}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" align="center">
                        Total Clicks
                      </Typography>
                      <Typography variant="h3" align="center" color="primary">
                        {analyticsData.totalClicks}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" align="center">
                        Click-Through Rate
                      </Typography>
                      <Typography variant="h3" align="center" color="primary">
                        {(analyticsData.clickThroughRate * 100).toFixed(1)}%
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Platform Breakdown */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  Performance by Platform
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Platform</TableCell>
                        <TableCell align="right">Shares</TableCell>
                        <TableCell align="right">Clicks</TableCell>
                        <TableCell align="right">CTR</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(analyticsData.sharesByPlatform).map(([platform, shares]) => {
                        const clicks = analyticsData.clicksByPlatform[platform as SocialPlatform] || 0;
                        const ctr = shares > 0 ? (clicks / shares) * 100 : 0;
                        
                        // Skip platforms with no shares
                        if (shares === 0) return null;
                        
                        return (
                          <TableRow key={platform}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {platform === 'facebook' && <FacebookIcon fontSize="small" style={{ color: '#1877F2', marginRight: 8 }} />}
                                {platform === 'twitter' && <TwitterIcon fontSize="small" style={{ color: '#1DA1F2', marginRight: 8 }} />}
                                {platform === 'linkedin' && <LinkedInIcon fontSize="small" style={{ color: '#0A66C2', marginRight: 8 }} />}
                                {platform === 'instagram' && <InstagramIcon fontSize="small" style={{ color: '#E4405F', marginRight: 8 }} />}
                                {platform === 'email' && <EmailIcon fontSize="small" style={{ color: '#EA4335', marginRight: 8 }} />}
                                {platform === 'whatsapp' && <WhatsAppIcon fontSize="small" style={{ color: '#25D366', marginRight: 8 }} />}
                                {platform === 'direct' && <LinkIcon fontSize="small" style={{ color: '#5E35B1', marginRight: 8 }} />}
                                {platform.charAt(0).toUpperCase() + platform.slice(1)}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{shares}</TableCell>
                            <TableCell align="right">{clicks}</TableCell>
                            <TableCell align="right">{ctr.toFixed(1)}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Timeline */}
                {Object.keys(analyticsData.sharesByDate).length > 0 && (
                  <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                      Timeline
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Shares</TableCell>
                            <TableCell align="right">Clicks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(analyticsData.sharesByDate)
                            .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
                            .map(([date, shares]) => {
                              const clicks = analyticsData.clicksByDate[date] || 0;
                              
                              return (
                                <TableRow key={date}>
                                  <TableCell>{format(new Date(date), 'MMM d, yyyy')}</TableCell>
                                  <TableCell align="right">{shares}</TableCell>
                                  <TableCell align="right">{clicks}</TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            ) : (
              <Typography align="center" color="text.secondary" sx={{ my: 4 }}>
                No analytics data available for this asset yet. Try sharing it on social media first!
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnalyticsDialogOpen(false)}>Close</Button>
            <Button 
              color="primary" 
              startIcon={<ShareIcon />}
              onClick={(e) => {
                setAnalyticsDialogOpen(false);
                if (selectedAsset) {
                  handleShareMenuOpen(e as React.MouseEvent<HTMLElement>, selectedAsset);
                }
              }}
            >
              Share Now
            </Button>
          </DialogActions>
        </Dialog>

        {/* Copy Success Snackbar */}
        {copySuccess && (
          <Box 
            sx={{ 
              position: 'fixed', 
              bottom: 20, 
              left: '50%', 
              transform: 'translateX(-50%)',
              backgroundColor: 'success.main',
              color: 'white',
              py: 1,
              px: 3,
              borderRadius: 1
            }}
          >
            <Typography>{copySuccess}</Typography>
          </Box>
        )}

        {/* Share Success Snackbar */}
        {shareSuccess && (
          <Snackbar
            open={Boolean(shareSuccess)}
            autoHideDuration={3000}
            onClose={() => setShareSuccess(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert severity="success" variant="filled">
              {shareSuccess}
            </Alert>
          </Snackbar>
        )}
    </ResponsiveContainer>
  );
};

export default EventAssetsTable;
